import React, { lazy, Suspense, useEffect, useLayoutEffect, useRef } from "react";
import { Routes, Route, useLocation, useNavigationType } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Home from "../pages/Home";
import { getPageLoaderId, hidePageLoader, showPageLoader, waitForCurrentPageAssets } from "./pageLoadStatus";

export const pageLoaders = {
  services: () => import("../pages/Services"),
  about: () => import("../pages/About"),
  cars: () => import("../pages/AvadCars"),
  agro: () => import("../pages/AvadAgro"),
  contact: () => import("../pages/ContactForm"),
  naturalResources: () => import("../pages/NaturalResources"),
  industrial: () => import("../pages/Industrial"),
  investments: () => import("../pages/Investments"),
  notFound: () => import("../pages/NotFound"),
  realEstate: () => import("../pages/RealEstate"),
  marketing: () => import("../pages/Marketing"),
  homeLife: () => import("../pages/HomeLife"),
  construction: () => import("../pages/Construction"),
};

const Services = lazy(pageLoaders.services);
const About = lazy(pageLoaders.about);
const AvadCars = lazy(pageLoaders.cars);
const AvadAgro = lazy(pageLoaders.agro);
const ContactForm = lazy(pageLoaders.contact);
const NaturalResources = lazy(pageLoaders.naturalResources);
const Industrial = lazy(pageLoaders.industrial);
const Investments = lazy(pageLoaders.investments);
const NotFound = lazy(pageLoaders.notFound);
const RealEstate = lazy(pageLoaders.realEstate);
const Marketing = lazy(pageLoaders.marketing);
const HomeLife = lazy(pageLoaders.homeLife);
const Construction = lazy(pageLoaders.construction);


const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.3 } },
};

const ROUTE_EXIT_SETTLE_MS = 360;

const AnimatedPage = ({ children, routePath, shouldScrollToTop }) => {
  useEffect(() => {
    let isCancelled = false;
    const loaderId = getPageLoaderId();

    const waitTimer = window.setTimeout(() => {
      waitForCurrentPageAssets().then(() => {
        if (!isCancelled) {
          if (shouldScrollToTop) {
            window.scrollTo({ top: 0, left: 0, behavior: "auto" });
          }
          hidePageLoader({ loaderId });
        }
      });
    }, ROUTE_EXIT_SETTLE_MS);

    return () => {
      isCancelled = true;
      window.clearTimeout(waitTimer);
    };
  }, [routePath, shouldScrollToTop]);

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit">
      {children}
    </motion.div>
  );
};

const RouteFallback = () => (
  <main className="min-h-[calc(100vh-80px)] bg-white" aria-hidden="true" />
);

const AnimatedRoutes = ({ currentLang }) => {
  const location = useLocation();
  const previousPath = useRef(location.pathname);

  const navigationType = useNavigationType();
  const shouldScrollToTop = navigationType !== "POP";

  useLayoutEffect(() => {
    if (previousPath.current === location.pathname) {
      return undefined;
    }

    previousPath.current = location.pathname;
    showPageLoader();
  }, [location.pathname]);

  const routes = [
    { path: "/", Component: Home },
    { path: "/services", Component: Services },
    { path: "/about", Component: About },
    { path: "/cars", Component: AvadCars },
    { path: "/contactform", Component: ContactForm },
    { path: "/naturalres", Component: NaturalResources },
    { path: "/industrial", Component: Industrial },
    { path: "/investments", Component: Investments },
    { path: "/agro", Component: AvadAgro },
    { path: "/realestate", Component: RealEstate },
    { path: "/marketing", Component: Marketing },
    { path: "/homeLife", Component: HomeLife },
    { path: "/homelife", Component: HomeLife },
    { path: "/construction", Component: Construction },
  ];

  return (
    <Suspense fallback={<RouteFallback />}>
      <AnimatePresence mode="wait">
        <Routes location={location} key={location.pathname}>
          {routes.map(({ path, Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <AnimatedPage routePath={location.pathname} shouldScrollToTop={shouldScrollToTop}>
                  <Component currentLang={currentLang} />
                </AnimatedPage>
              }
            />
          ))}
          <Route path="*" element={<AnimatedPage routePath={location.pathname}><NotFound /></AnimatedPage>} />
        </Routes>
      </AnimatePresence>
    </Suspense>
  );
};

export default AnimatedRoutes;
